import { Path, Operation } from 'slate';
import { defineTransform } from '../transform';

import {
  adjustPath,
  pathWasRemoved,
  adjustNodePathForNodeMove,
  adjustPathForNodeSplit,
  adjustPathForNodeMerge,
  adjustPathForNodeInsert,
  adjustPathForNodeRemove,
  updateRemovedNodeWithOperation,
} from '../utils';

defineTransform('remove_node', 'insert_text', (op1, op2, side) => {
  if (pathWasRemoved(op2.path, op1.path)) {
    return {
      ...op1,
      node: updateRemovedNodeWithOperation(op1, op2),
    };
  }
  return op1;
});
defineTransform('remove_node', 'remove_text', (op1, op2, side) => {
  if (pathWasRemoved(op2.path, op1.path)) {
    return {
      ...op1,
      node: updateRemovedNodeWithOperation(op1, op2),
    };
  }
  return op1;
});

defineTransform('remove_node', 'split_node', (op1, op2, side) => {
  const tOp = { ...op1 };

  if (Path.isAncestor(op1.path, op2.path)) {
    return {
      ...op1,
      node: updateRemovedNodeWithOperation(op1, op2),
    };
  }

  if (Path.equals(op1.path, op2.path)) {
    const removeBeforeSplit = tOp;
    const removeAfterSplit = tOp;
    return [removeBeforeSplit, removeAfterSplit];
  }

  tOp.path = adjustPathForNodeSplit(op1.path, -1, op2, side === 'left');
  return tOp;
});

defineTransform('remove_node', 'merge_node', (op1, op2, side) => {
  const tOp = { ...op1 };

  if (Path.isAncestor(op1.path, op2.path)) {
    return {
      ...op1,
      node: updateRemovedNodeWithOperation(op1, op2),
    };
  }

  // If the newly merged node includes part of the node we're
  // removing, we want to split it out before removing so we only
  // remove the part we're expecting to.
  if (
    Path.equals(op1.path, op2.path) ||
    Path.equals(op1.path, adjustPath(op2.path, -1, op2.path.length - 1))
  ) {
    const splitNodeOperation = Operation.inverse(op2);
    return [splitNodeOperation, tOp];
  }

  tOp.path = adjustPathForNodeMerge(op1.path, op2);

  return tOp;
});

defineTransform('remove_node', 'move_node', (op1, op2, side) => {
  const tOp = { ...op1 };
  tOp.path = adjustNodePathForNodeMove(op1.path, op2);

  if (
    pathWasRemoved(op2.path, op1.path) &&
    !Path.equals(op1.path, op2.path) &&
    !Path.isAncestor(op1.path, op2.newPath)
  ) {
    // If the source was removed, I'm making the call not to add it to
    // the destination. Hopefully that's OK. Otherwise I'll also need
    // to change move_node, remove_node, and reverting the remove
    // would duplicate the moved node.
    //
    // But, if we're moving a node from our subtree to our subtree,
    // it'll get deleted anyway, so we can skip the invert. It might
    // do something weird if you undo the op, though.
    const removeNodeOp = { ...tOp };
    const adjustedOp2Path = adjustNodePathForNodeMove(op2.path, op2);
    const removeChildOp = {
      ...tOp,
      path: adjustPathForNodeRemove(adjustedOp2Path, removeNodeOp, false),
    };

    return [removeNodeOp, removeChildOp];
  }
  return tOp;
});

defineTransform('remove_node', 'insert_node', (op1, op2, side) => {
  if (Path.isAncestor(op1.path, op2.path)) {
    return {
      ...op1,
      node: updateRemovedNodeWithOperation(op1, op2),
    };
  }

  return {
    ...op1,
    path: adjustPathForNodeInsert(op1.path, op2, true),
  };
});

defineTransform('remove_node', 'remove_node', (op1, op2, side) => {
  const tOp = { ...op1 };

  if (Path.isAncestor(op1.path, op2.path)) {
    return {
      ...op1,
      node: updateRemovedNodeWithOperation(op1, op2),
    };
  } else if (pathWasRemoved(op1.path, op2.path)) {
    return;
  }

  tOp.path = adjustPathForNodeRemove(op1.path, op2, false);

  return tOp;
});

defineTransform('remove_node', 'set_node', (op1, op2, side) => {
  if (Path.equals(op1.path, op2.path) || Path.isAncestor(op1.path, op2.path)) {
    return {
      ...op1,
      node: updateRemovedNodeWithOperation(op1, op2),
    };
  }
  return op1;
});
defineTransform('remove_node', 'add_mark', (op1, op2, side) => op1);
defineTransform('remove_node', 'remove_mark', (op1, op2, side) => op1);
defineTransform('remove_node', 'set_mark', (op1, op2, side) => op1);
defineTransform('remove_node', 'add_annotation', (op1, op2, side) => op1);
defineTransform('remove_node', 'remove_annotation', (op1, op2, side) => op1);
defineTransform('remove_node', 'set_annotation', (op1, op2, side) => op1);
defineTransform('remove_node', 'set_selection', (op1, op2, side) => op1);
defineTransform('remove_node', 'add_metadata', (op1, op2, side) => op1);
defineTransform('remove_node', 'remove_metadata', (op1, op2, side) => op1);
defineTransform('remove_node', 'set_metadata', (op1, op2, side) => op1);
